// Utility for formatting dates according to system locale

// Get the user's system locale - with timezone-based fallback
export const getSystemLocale = () => {
  const browserLocale = navigator.language || navigator.userLanguage || 'en-US';
  
  // If browser reports en-US but timezone is European, override to en-GB
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isEuropeanTimezone = timezone && timezone.startsWith('Europe/');
  
  if (browserLocale === 'en-US' && isEuropeanTimezone) {
    console.log('Detected European timezone with en-US locale, using en-GB instead');
    return 'en-GB';
  }
  
  return browserLocale;
};

// Check if system uses 24-hour format
export const uses24HourFormat = () => {
  // Check timezone - European timezones typically use 24-hour
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isEuropeanTimezone = timezone && timezone.startsWith('Europe/');
  const isAsianTimezone = timezone && (timezone.startsWith('Asia/') || timezone.startsWith('Africa/'));
  
  // Most of the world outside US uses 24-hour format
  if (isEuropeanTimezone || isAsianTimezone) {
    return true;
  }
  
  const locale = getSystemLocale();
  // Create a test date and check if it includes AM/PM
  const testDate = new Date(2020, 0, 1, 13, 0, 0);
  const formatted = testDate.toLocaleTimeString(locale);
  // If it doesn't include AM/PM, it's 24-hour format
  return !formatted.match(/AM|PM/i);
};

// Check date format order (DD/MM vs MM/DD)
export const getDateOrder = () => {
  const locale = getSystemLocale();
  
  // Check timezone for additional hint
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const isEuropeanTimezone = timezone && timezone.startsWith('Europe/');
  const isAsianTimezone = timezone && (timezone.startsWith('Asia/') || timezone.startsWith('Africa/'));
  
  // If we're in a European or Asian timezone, use DMY regardless of browser locale
  if (isEuropeanTimezone || isAsianTimezone) {
    return 'DMY';
  }
  
  // For Americas, check more carefully
  const testDate = new Date(2020, 0, 15); // January 15, 2020
  const formatted = testDate.toLocaleDateString(locale);
  
  // Check if the day (15) comes before the month (01)
  const dayIndex = formatted.indexOf('15');
  const monthIndex = formatted.search(/01|1/);
  
  if (dayIndex > -1 && monthIndex > -1) {
    return dayIndex < monthIndex ? 'DMY' : 'MDY';
  }
  
  // Fallback based on locale
  if (locale === 'en-US') return 'MDY';
  return 'DMY'; // Most of the world uses DMY
};

// Format date according to system locale
export const formatDateTime = (date) => {
  const locale = getSystemLocale();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Use Intl.DateTimeFormat for proper localization
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: !uses24HourFormat()
  };
  
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

// Format time only
export const formatTime = (date) => {
  const locale = getSystemLocale();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const options = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: !uses24HourFormat()
  };
  
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

// Format date only
export const formatDate = (date) => {
  const locale = getSystemLocale();
  const dateObj = date instanceof Date ? date : new Date(date);
  
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  };
  
  return new Intl.DateTimeFormat(locale, options).format(dateObj);
};

// Get moment format string based on system locale
export const getMomentFormat = () => {
  const is24Hour = uses24HourFormat();
  const dateOrder = getDateOrder();
  
  const dateFormat = dateOrder === 'DMY' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  const timeFormat = is24Hour ? 'HH:mm' : 'h:mm A';
  
  return {
    date: dateFormat,
    time: timeFormat,
    datetime: `${dateFormat} ${timeFormat}`
  };
};

// Get a description of the current system format for display
export const getSystemFormatDescription = () => {
  const is24Hour = uses24HourFormat();
  const dateOrder = getDateOrder();
  const locale = getSystemLocale();
  
  const dateFormat = dateOrder === 'DMY' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
  const timeFormat = is24Hour ? '24-hour' : '12-hour (AM/PM)';
  
  return `${dateFormat}, ${timeFormat} (${locale})`;
};